import React, { useEffect, useRef, useState } from 'react';
import url from './url';

const CONDITIONS = [
    ['AllNew', 'All new'],
    ['AllUsed', 'All used'],
    ['BothNewAndUsed', 'A mix of new and used'],
];

const categoryValue = (category) => {
    if (typeof category === 'string' || typeof category === 'number') return category;
    return category?.value
        ?? category?.category
        ?? category?.Category
        ?? category?.name
        ?? category?.Name
        ?? category?.label
        ?? category?.CategoryName
        ?? category?.id
        ?? category?.CategoryId;
};

const categoryLabel = (category) => (
    typeof category === 'string' || typeof category === 'number'
        ? String(category)
        : category?.label
            ?? category?.name
            ?? category?.Name
            ?? category?.CategoryName
            ?? category?.category
            ?? String(categoryValue(category) ?? '')
);

function ShipmentProtection({
    quoteRequestBody,
    carrierServiceId,
    goodsValue,
    initialInsurance,
    onInsuranceChange,
    selectionOnly = false,
    summaryOnly = false,
}) {
    const [enabled, setEnabled] = useState(Boolean(initialInsurance));
    const [coverage, setCoverage] = useState(initialInsurance?.coverage ?? (goodsValue || ''));
    const [category, setCategory] = useState(initialInsurance?.category ?? '');
    const [condition, setCondition] = useState(initialInsurance?.condition || 'BothNewAndUsed');
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState('');
    const [price, setPrice] = useState(null);
    const [priceState, setPriceState] = useState('idle');
    const [priceError, setPriceError] = useState('');
    const requestIdRef = useRef(0);
    const currentChoiceRef = useRef({ enabled: Boolean(initialInsurance), coverage: initialInsurance?.coverage ?? (goodsValue || ''), category: initialInsurance?.category ?? '', condition: initialInsurance?.condition || 'BothNewAndUsed' });
    const callbackRef = useRef(onInsuranceChange);
    const previousGoodsValueRef = useRef(goodsValue);

    callbackRef.current = onInsuranceChange;

    useEffect(() => {
        const controller = new AbortController();
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) headers.Authorization = `Bearer ${token}`;

        const loadCategories = async () => {
            setCategoriesLoading(true);
            setCategoriesError('');
            try {
                const response = await fetch(`${url}/api/insurance/categories`, { headers, signal: controller.signal });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(data.message || data.error || 'Unable to load insurance categories.');
                if (!Array.isArray(data.categories)) throw new Error('Insurance categories were not returned in the expected format.');
                setCategories(data.categories.filter((item) => (
                    categoryValue(item) !== undefined && categoryValue(item) !== null && categoryValue(item) !== ''
                )));
            } catch (error) {
                if (error.name !== 'AbortError') setCategoriesError(error.message || 'Unable to load insurance categories.');
            } finally {
                if (!controller.signal.aborted) setCategoriesLoading(false);
            }
        };

        loadCategories();
        return () => controller.abort();
    }, []);

    const updateChoice = (nextChoice, nextEnabled = enabled) => {
        const normalized = { ...nextChoice, enabled: nextEnabled };
        currentChoiceRef.current = normalized;
        requestIdRef.current += 1;
        setPrice(null);
        setPriceError('');
        setPriceState(nextEnabled ? 'waiting' : 'idle');
        if (nextEnabled) {
            const numericCoverage = Number(normalized.coverage);
            const declaredValue = Number(goodsValue);
            const categoryAvailable = categories.some((item) => String(categoryValue(item)) === String(normalized.category));
            const validSelection = Number.isFinite(numericCoverage)
                && numericCoverage > 0
                && (!Number.isFinite(declaredValue) || numericCoverage <= declaredValue)
                && categoryAvailable
                && CONDITIONS.some(([value]) => value === normalized.condition);
            callbackRef.current({
                coverage: normalized.coverage,
                category: normalized.category,
                condition: normalized.condition,
                premium: null,
            }, selectionOnly ? validSelection : false);
        } else {
            callbackRef.current(null, true);
        }
    };

    useEffect(() => {
        const previousValue = Number(previousGoodsValueRef.current);
        const declaredValue = Number(goodsValue);
        if (previousGoodsValueRef.current !== goodsValue && !enabled) {
            setCoverage(goodsValue || '');
        }
        if (previousGoodsValueRef.current !== goodsValue && enabled
            && Number.isFinite(previousValue) && previousValue > 0
            && Number.isFinite(declaredValue) && declaredValue > 0
            && Number(coverage) === previousValue) {
            const nextCoverage = String(goodsValue);
            setCoverage(nextCoverage);
            updateChoice({ coverage: nextCoverage, category, condition });
        }
        previousGoodsValueRef.current = goodsValue;
        // Updating only when the declared amount changes keeps custom coverage untouched.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goodsValue]);

    useEffect(() => {
        if (!enabled) return undefined;

        const pendingChoice = { coverage, category, condition, premium: null };
        currentChoiceRef.current = { ...pendingChoice, enabled: true };
        callbackRef.current(pendingChoice, false);

        if (selectionOnly) {
            const numericCoverage = Number(coverage);
            const declaredValue = Number(goodsValue);
            const selectedCategory = categories.find((item) => String(categoryValue(item)) === String(category));
            const validSelection = Number.isFinite(numericCoverage)
                && numericCoverage > 0
                && (!Number.isFinite(declaredValue) || numericCoverage <= declaredValue)
                && Boolean(selectedCategory)
                && CONDITIONS.some(([value]) => value === condition)
                && !categoriesLoading
                && !categoriesError;
            callbackRef.current(pendingChoice, validSelection);
            return undefined;
        }

        const numericCoverage = Number(coverage);
        if (!coverage || !Number.isFinite(numericCoverage) || numericCoverage <= 0 || !category || !CONDITIONS.some(([value]) => value === condition)) {
            setPrice(null);
            setPriceState('idle');
            setPriceError('');
            return undefined;
        }

        const hasDeclaredGoodsValue = goodsValue !== null && goodsValue !== undefined && goodsValue !== '';
        const declaredGoodsValue = hasDeclaredGoodsValue ? Number(goodsValue) : null;
        if (Number.isFinite(declaredGoodsValue) && numericCoverage > declaredGoodsValue) {
            setPrice(null);
            setPriceState('error');
            setPriceError(`Coverage cannot exceed the declared goods value of $${declaredGoodsValue.toFixed(2)} AUD.`);
            return undefined;
        }

        if (categoriesLoading) {
            setPriceState('waiting');
            return undefined;
        }

        if (categoriesError) {
            setPriceState('error');
            return undefined;
        }

        const selectedCategory = categories.find((item) => String(categoryValue(item)) === String(category));
        if (!selectedCategory) {
            setPriceState('error');
            setPriceError('Choose a category from the available insurance categories to get a price.');
            return undefined;
        }

        const choiceKey = JSON.stringify([numericCoverage, categoryValue(selectedCategory), condition, carrierServiceId]);
        const requestId = ++requestIdRef.current;
        setPrice(null);
        setPriceError('');
        setPriceState('loading');
        const timer = window.setTimeout(async () => {
            try {
                const response = await fetch(`${url}/api/insurance/price`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}),
                    },
                    body: JSON.stringify({
                        quoteRequestBody,
                        carrierServiceId,
                        insurance: {
                            coverage: numericCoverage,
                            category: categoryValue(selectedCategory),
                            condition,
                        },
                    }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(data.message || data.error || 'Unable to price shipment insurance.');
                const premium = Number(data.premium);
                const responseMatches = Number(data.coverage) === numericCoverage
                    && String(data.category) === String(categoryValue(selectedCategory))
                    && data.condition === condition;
                if (data.premium === null || data.premium === undefined || data.premium === ''
                    || !Number.isFinite(premium) || premium < 0 || !responseMatches) {
                    throw new Error('The insurance price did not match your current coverage. Please try again.');
                }
                const current = currentChoiceRef.current;
                const currentCategory = categories.find((item) => String(categoryValue(item)) === String(current.category));
                const currentKey = JSON.stringify([
                    Number(current.coverage),
                    currentCategory ? categoryValue(currentCategory) : current.category,
                    current.condition,
                    carrierServiceId,
                ]);
                if (requestId !== requestIdRef.current || !current.enabled || currentKey !== choiceKey) return;

                const pricedInsurance = {
                    coverage: data.coverage,
                    category: data.category,
                    condition: data.condition,
                    premium,
                };
                setPrice(pricedInsurance);
                setPriceState('ready');
                callbackRef.current(pricedInsurance, true);
            } catch (error) {
                if (requestId !== requestIdRef.current) return;
                setPrice(null);
                setPriceState('error');
                setPriceError(error.message || 'Unable to price shipment insurance.');
                const current = currentChoiceRef.current;
                callbackRef.current({
                    coverage: current.coverage,
                    category: current.category,
                    condition: current.condition,
                    premium: null,
                }, false);
            }
        }, 250);

        return () => {
            window.clearTimeout(timer);
            requestIdRef.current += 1;
        };
    }, [enabled, coverage, category, condition, categories, categoriesLoading, categoriesError, quoteRequestBody, carrierServiceId, goodsValue, selectionOnly]);

    const handleToggle = (event) => {
        const isEnabled = event.target.checked;
        setEnabled(isEnabled);
        updateChoice({ coverage, category, condition }, isEnabled);
    };

    const handleCoverageChange = (event) => {
        const value = event.target.value;
        setCoverage(value);
        updateChoice({ coverage: value, category, condition });
    };

    const handleCategoryChange = (event) => {
        const value = event.target.value;
        setCategory(value);
        updateChoice({ coverage, category: value, condition });
    };

    const handleConditionChange = (event) => {
        const value = event.target.value;
        setCondition(value);
        updateChoice({ coverage, category, condition: value });
    };

    const declaredGoodsValue = goodsValue !== null && goodsValue !== undefined && goodsValue !== ''
        ? Number(goodsValue)
        : null;
    const coverageExceedsDeclaredValue = Number.isFinite(declaredGoodsValue) && Number(coverage) > declaredGoodsValue;
    const selectionValid = Number.isFinite(Number(coverage))
        && Number(coverage) > 0
        && !coverageExceedsDeclaredValue
        && Boolean(category)
        && CONDITIONS.some(([value]) => value === condition);
    const statusText = priceState === 'loading' || (priceState === 'waiting' && selectionValid && !categoriesLoading)
        ? 'Calculating your insurance price…'
        : priceState === 'ready' && price
            ? `Insurance premium: $${price.premium.toFixed(2)} AUD (including tax)`
            : '';
    const summaryCoverage = Number(coverage);

    if (summaryOnly) {
        return (
            <section className="shipment-protection shipment-protection-summary" aria-labelledby="shipment-protection-summary-title">
                <div className="shipment-protection-heading">
                    <div>
                        <span className="section-kicker">OPTIONAL COVER</span>
                        <h3 id="shipment-protection-summary-title">Paid insurance</h3>
                        {enabled ? (
                            <p>
                                Selected: {Number.isFinite(summaryCoverage) && summaryCoverage > 0 ? `$${summaryCoverage.toFixed(2)} AUD` : 'coverage amount incomplete'} total goods coverage · {categoryLabel(categories.find((item) => String(categoryValue(item)) === String(category)) || category)} · {CONDITIONS.find(([value]) => value === condition)?.[1] || condition}.
                            </p>
                        ) : (
                            <p>No additional paid insurance selected.</p>
                        )}
                    </div>
                </div>
                {enabled && categoriesError && <p className="shipment-protection-message is-error" role="alert">{categoriesError}</p>}
                {enabled && !categoriesLoading && !categoriesError && categories.length === 0 && (
                    <p className="shipment-protection-message is-error" role="alert">No insurance categories are currently available. Paid insurance cannot be added to this shipment at the moment.</p>
                )}
                {enabled && priceError && <p className="shipment-protection-message is-error" role="alert">{priceError}</p>}
                {enabled && statusText && <p className="shipment-protection-message" role="status">{statusText}</p>}
                {enabled && !statusText && !priceError && !categoriesError && (categoriesLoading || categories.length > 0) && (
                    <p className="shipment-protection-message" role="status">Calculating your insurance price…</p>
                )}
                <small className="shipment-protection-summary-terms">
                    Paid cover is subject to provider eligibility and terms. The selected courier’s included warranty is shown separately above.
                </small>
            </section>
        );
    }

    return (
        <section className={`shipment-protection${selectionOnly ? ' shipment-protection-first-step' : ''}`} aria-labelledby="shipment-protection-title">
            <div className="shipment-protection-heading">
                <div>
                    <span className="section-kicker">OPTIONAL COVER</span>
                    <h3 id="shipment-protection-title">Shipment protection</h3>
                    <p>Add paid protection for the goods in this shipment. No extra cover is selected by default.</p>
                </div>
                {!summaryOnly && (
                    <label className="shipment-protection-toggle">
                        <input id="shipment-protection-toggle" type="checkbox" checked={enabled} onChange={handleToggle} />
                        <span>Add paid insurance</span>
                    </label>
                )}
            </div>

            {selectionOnly && Number(goodsValue) > 250 && (
                <p id="goods-value-insurance-warning" className="shipment-protection-notice" role="note">
                    Your goods value exceeds $250. Only up to $250 is covered by complimentary cover, where offered by the selected courier and subject to its terms. You need to purchase insurance to cover a higher goods value.
                </p>
            )}

            {enabled && !summaryOnly && (
                <div className="shipment-protection-fields">
                    <label>
                        Total goods coverage (AUD)
                        <input
                            type="number"
                            min="0.01"
                            max={Number.isFinite(declaredGoodsValue) ? declaredGoodsValue : undefined}
                            step="0.01"
                            inputMode="decimal"
                            value={coverage}
                            onChange={handleCoverageChange}
                            aria-invalid={coverageExceedsDeclaredValue}
                            aria-describedby={coverageExceedsDeclaredValue
                                ? 'shipment-protection-coverage-error'
                                : undefined}
                        />
                    </label>
                    <label>
                        Goods category
                        <select value={String(category)} onChange={handleCategoryChange} disabled={categoriesLoading || categories.length === 0}>
                            <option value="">{categoriesLoading ? 'Loading categories…' : 'Select a category'}</option>
                            {categories.map((item) => (
                                <option key={String(categoryValue(item))} value={String(categoryValue(item))}>{categoryLabel(item)}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Goods condition
                        <select value={condition} onChange={handleConditionChange}>
                            {CONDITIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                    </label>
                </div>
            )}

            {!summaryOnly && categoriesError && <p className="shipment-protection-message is-error" role="alert">{categoriesError}</p>}
            {!summaryOnly && !categoriesLoading && !categoriesError && categories.length === 0 && (
                <p className="shipment-protection-message is-error" role="alert">No insurance categories are currently available. Paid insurance cannot be added to this shipment at the moment.</p>
            )}
            {enabled && !selectionOnly && priceError && <p className="shipment-protection-message is-error" role="alert">{priceError}</p>}
            {enabled && !selectionOnly && statusText && <p className="shipment-protection-message" role="status">{statusText}</p>}
            {enabled && selectionOnly && <p className="shipment-protection-message" role="status">Premium calculated after you choose a courier and included in Review &amp; Pay.</p>}
            {enabled && selectionOnly && coverageExceedsDeclaredValue && (
                <p id="shipment-protection-coverage-error" className="shipment-protection-message is-error" role="alert">
                    Coverage cannot exceed the declared goods value of ${declaredGoodsValue.toFixed(2)} AUD.
                </p>
            )}
            {enabled && selectionOnly && !selectionValid && !coverageExceedsDeclaredValue && (
                <p className="shipment-protection-message" role="note">Enter a coverage amount, category, and goods condition before getting quotes.</p>
            )}
            {enabled && !selectionOnly && !selectionValid && !coverageExceedsDeclaredValue && <p className="shipment-protection-message" role="note">Enter a coverage amount, category, and goods condition to calculate the premium.</p>}
        </section>
    );
}

export default ShipmentProtection;